/**
 * Decouple verification logger (plan phase B.3.v).
 *
 * Runtime-gated logger for verifying that B.2's per-stream rotation decouple
 * actually translates to correct frontend playback. The 7 instrumentation
 * points scattered across StreamSlot / TimelineManager / the shared render worker
 * / TranscodePage / OfflinePage route through this module so the `[decouple]`
 * prefix appears in DevTools consistently.
 *
 * Activation (pick either):
 *   - `localStorage.setItem('sych-debug-decouple', 'true')` — persists
 *     across reloads; works for main-thread code and any worker spawned
 *     with a URL that inherits localStorage (not all workers do).
 *   - Append `?debug=decouple` to the page URL — reliable everywhere
 *     including dedicated workers that don't have localStorage. Workers
 *     must be launched with this query in their URL for the flag to carry.
 *
 * Log levels:
 *   - `info` / `debug` — gated by the flag; compiled to no-ops in
 *     production unless the flag is active at call time.
 *   - `warn` — always fires. This is how regression-signal instrumentation
 *     (pair-duration mismatch, dual-clock gap outliers, aggregate drift)
 *     survives production tree-shake even without the flag.
 */

const FLAG_KEY = 'sych-debug-decouple';
const FLAG_PARAM = 'debug=decouple';
const PREFIX = '[decouple]';
const STYLE = 'color: #E64A19; font-weight: bold';

// `window` is only defined in the main thread; workers expose `self` but not
// `window`. Use `typeof window` to pick the correct console formatting path.
const IS_WORKER = typeof window === 'undefined';

function readLocationSearch(): string {
  try {
    if (typeof location !== 'undefined' && location.search) return location.search;
  } catch {
    /* location may throw in some sandbox contexts */
  }
  try {
    // Workers: `self.location.search` is the worker URL's query, which picks
    // up `?debug=decouple` if the parent main.tsx/transcode-main.tsx appends it.
    if (typeof self !== 'undefined' && self && 'location' in self) {
      const loc = (self as unknown as { location?: { search?: string } }).location;
      if (loc?.search) return loc.search;
    }
  } catch {
    /* ignore */
  }
  return '';
}

function flagEnabled(): boolean {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem(FLAG_KEY) === 'true') {
      return true;
    }
  } catch {
    /* localStorage throws in some worker contexts — fall through to URL check */
  }
  if (readLocationSearch().includes(FLAG_PARAM)) return true;
  return false;
}

// Cache the flag so we don't hit localStorage / parse URLs on every log call.
// The cost of a miss is a dead function call, so caching is a straight win.
let _cached: boolean | null = null;
function isEnabled(): boolean {
  if (_cached === null) _cached = flagEnabled();
  return _cached;
}

/**
 * Force re-read of the flag. Only useful for tests — in real sessions the
 * flag state is fixed at page load.
 */
export function resetDecoupleFlagCache(): void {
  _cached = null;
}

/**
 * Programmatic override of the flag. Used by workers that receive the flag
 * via a postMessage from the main thread — dedicated workers don't share
 * localStorage with the main document and their `self.location.search` is
 * the worker script URL, not the main document URL, so URL-based detection
 * doesn't carry. The main thread detects the flag at startup and posts a
 * config message to every spawned worker which calls this setter.
 */
export function setDecoupleFlag(enabled: boolean): void {
  _cached = enabled;
}

function formatArgs(message: string, data: unknown): unknown[] {
  const hasData = data !== undefined;
  if (IS_WORKER) {
    return hasData ? [PREFIX, message, data] : [PREFIX, message];
  }
  return hasData ? [`%c${PREFIX}`, STYLE, message, data] : [`%c${PREFIX}`, STYLE, message];
}

export const decoupleLog = {
  /**
   * Flag-gated informational log. Compiled to a no-op call when the flag
   * is not set; the inner `console.info` is never invoked.
   */
  info(message: string, data?: unknown): void {
    if (!isEnabled()) return;
    console.info(...formatArgs(message, data));
  },

  /**
   * Flag-gated detailed log. Same gating as `info`. Use for per-tick /
   * throttled high-volume signals.
   */
  debug(message: string, data?: unknown): void {
    if (!isEnabled()) return;
    console.log(...formatArgs(message, data));
  },

  /**
   * Always-fires warning. Use for regression signals (pair mismatch,
   * dual-clock gap, aggregate drift) — these need to reach the operator
   * even when no one turned the flag on.
   */
  warn(message: string, data?: unknown): void {
    console.warn(...formatArgs(message, data));
  },

  /** Whether the flag is currently active. Useful for gating expensive
   *  data-prep that would otherwise be wasted. */
  get enabled(): boolean {
    return isEnabled();
  },
};

/**
 * Emit a one-line banner once per session when the flag is active. Intended
 * to be called from AppShell mount; gives a grep-anchor in DevTools so
 * filtering on `[decouple]` catches everything from that session onward.
 */
export function logDecoupleBanner(context: {
  appVersion?: string;
  bundleVersion?: string | number;
  where: string;
}): void {
  if (!isEnabled()) return;
  decoupleLog.info('verification logging enabled', context);
}

/**
 * Once-per-key helper. Prevents a log fired from a hot path (every frame,
 * every tick) from spamming. Keyed by a stable object (usually the
 * VideoContext or parser instance).
 *
 *   logOncePer(ctx, 'fps-site-435', () => decoupleLog.info(...));
 */
const _once = new WeakMap<object, Set<string>>();
export function logOncePer(key: object, tag: string, fn: () => void): void {
  let seen = _once.get(key);
  if (!seen) {
    seen = new Set();
    _once.set(key, seen);
  }
  if (seen.has(tag)) return;
  seen.add(tag);
  fn();
}

/**
 * Simple rate-limiter for hot-path logs. Used by the dual-clock sync
 * instrumentation to throttle emission to ~1 Hz even when the render
 * worker ticks at 30 Hz.
 */
export function createRateLimiter(intervalMs: number): () => boolean {
  let lastAt = 0;
  return () => {
    const now = Date.now();
    if (now - lastAt < intervalMs) return false;
    lastAt = now;
    return true;
  };
}
